// PKG-BETA-N8N-PV-TICKET — short-lived access ticket issued by the community
// forum for the visual-programming resource. The ticket is an opaque bearer:
// never persist it, log it, send it to analytics, or use it for another slug.

export const N8N_RESOURCE_SLUG = "programacion-visual";
export const N8N_RESOURCE_PATH = `/recursos/${N8N_RESOURCE_SLUG}`;
export const N8N_RESOURCE_TICKET_PARAM = "n8n";
export const DEFAULT_COMMUNITY_API_BASE = "https://comunidad.umbralbim.io/api";

const VERIFY_PATH = "umbral/n8n-resource-ticket/verify";
const MAX_TICKET_LENGTH = 1_024;
const DEFAULT_TIMEOUT_MS = 5_000;

export interface N8nResourceTicketEnv {
  readonly VITE_COMMUNITY_API_BASE?: string;
}

interface VerifyOptions {
  readonly env?: N8nResourceTicketEnv;
  readonly fetchImpl?: typeof fetch;
  readonly signal?: AbortSignal;
  readonly timeoutMs?: number;
}

let capturedTicket: string | null = null;
let preTelemetryFetch: typeof fetch | null = null;

function normalizeTicket(value: string | null): string | null {
  if (value === null) return null;

  const ticket = value.trim();
  if (!ticket || ticket.length > MAX_TICKET_LENGTH) return null;
  if (/[\u0000-\u001f\u007f]/.test(ticket)) return null;
  return ticket;
}

function relativeUrl(url: URL): string {
  return `${url.pathname}${url.search}${url.hash}`;
}

/**
 * Capture the ticket into process memory and remove it from the address bar.
 *
 * This must run before Sentry or PostHog initialise. Besides keeping the page
 * URL out of page-view telemetry, it snapshots the native fetch implementation
 * before either SDK can instrument requests; the verifier URL itself also
 * contains the bearer as required by the public forum GET contract.
 */
export function captureAndScrubN8nResourceTicket(
  location: Pick<Location, "href"> = window.location,
  history: Pick<History, "replaceState" | "state"> = window.history,
  fetchImpl: typeof fetch | undefined =
    typeof window !== "undefined" && typeof window.fetch === "function"
      ? window.fetch.bind(window)
      : undefined,
): string | null {
  if (!preTelemetryFetch && fetchImpl) preTelemetryFetch = fetchImpl;

  let url: URL;
  try {
    url = new URL(location.href);
  } catch {
    return capturedTicket;
  }

  if (!url.searchParams.has(N8N_RESOURCE_TICKET_PARAM)) return capturedTicket;

  const ticketValues = url.searchParams.getAll(N8N_RESOURCE_TICKET_PARAM);
  const ticket = ticketValues.length === 1 ? normalizeTicket(ticketValues[0]) : null;
  url.searchParams.delete(N8N_RESOURCE_TICKET_PARAM);

  // Scrub even on a wrong route. It is still bearer-shaped input and must not
  // reach telemetry, but it is captured only for the one authorised resource.
  history.replaceState(history.state, "", relativeUrl(url));

  const pathname = url.pathname.replace(/\/+$/, "") || "/";
  // An invalid or duplicated value on the exact route clears prior module
  // state as well. Re-entry must never inherit a stale granted capability.
  if (pathname === N8N_RESOURCE_PATH) capturedTicket = ticket;

  return capturedTicket;
}

/** In-memory only; deliberately no sessionStorage/localStorage fallback. */
export function getCapturedN8nResourceTicket(): string | null {
  return capturedTicket;
}

/**
 * Resolve the public forum API base. An explicit malformed override does not
 * silently fall back to LIVE: it returns null so verification fails closed.
 */
export function resolveCommunityApiBase(
  env: N8nResourceTicketEnv = import.meta.env,
): string | null {
  const configured = env.VITE_COMMUNITY_API_BASE?.trim();
  const raw = configured || DEFAULT_COMMUNITY_API_BASE;

  try {
    const url = new URL(raw);
    if (!/^https?:$/.test(url.protocol)) return null;
    if (
      url.protocol === "http:" &&
      url.hostname.toLowerCase() !== "localhost" &&
      url.hostname !== "127.0.0.1"
    ) {
      return null;
    }
    if (url.username || url.password || url.search || url.hash) return null;

    url.pathname = url.pathname.replace(/\/+$/, "") || "/";
    return url.toString().replace(/\/$/, "");
  } catch {
    return null;
  }
}

export function buildN8nResourceTicketVerifyUrl(
  ticket: string,
  env: N8nResourceTicketEnv = import.meta.env,
): URL | null {
  const normalizedTicket = normalizeTicket(ticket);
  const apiBase = resolveCommunityApiBase(env);
  if (!normalizedTicket || !apiBase) return null;

  const endpoint = new URL(`${apiBase}/`);
  endpoint.pathname = `${endpoint.pathname.replace(/\/$/, "")}/${VERIFY_PATH}`;
  endpoint.searchParams.set(N8N_RESOURCE_TICKET_PARAM, normalizedTicket);
  return endpoint;
}

/**
 * Ask the forum whether the ticket is valid *now*. The server rechecks current
 * n8n membership, so a revoked actor invalidates an otherwise unexpired ticket.
 * Every transport/config/schema failure resolves false without logging input.
 */
export async function verifyN8nResourceTicket(
  ticket: string,
  options: VerifyOptions = {},
): Promise<boolean> {
  const url = buildN8nResourceTicketVerifyUrl(ticket, options.env);
  const fetcher = options.fetchImpl ?? preTelemetryFetch ?? globalThis.fetch;
  if (!url || typeof fetcher !== "function") return false;

  const controller = new AbortController();
  const abortFromCaller = () => controller.abort();
  if (options.signal?.aborted) controller.abort();
  else options.signal?.addEventListener("abort", abortFromCaller, { once: true });

  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const timeout = timeoutMs > 0 ? globalThis.setTimeout(() => controller.abort(), timeoutMs) : null;

  try {
    const response = await fetcher(url, {
      method: "GET",
      cache: "no-store",
      credentials: "omit",
      redirect: "error",
      referrerPolicy: "no-referrer",
      signal: controller.signal,
    });
    if (response.status !== 200) return false;

    const payload: unknown = await response.json();
    const exactKeys =
      typeof payload === "object" &&
      payload !== null &&
      !Array.isArray(payload) &&
      Object.keys(payload).sort().join(",") === "ok,slug";
    return (
      exactKeys &&
      (payload as { ok?: unknown }).ok === true &&
      (payload as { slug?: unknown }).slug === N8N_RESOURCE_SLUG
    );
  } catch {
    return false;
  } finally {
    if (timeout !== null) globalThis.clearTimeout(timeout);
    options.signal?.removeEventListener("abort", abortFromCaller);
  }
}

/** Test-only reset; production never clears or persists the captured bearer. */
export function __resetN8nResourceTicketForTests(): void {
  capturedTicket = null;
  preTelemetryFetch = null;
}
